interface OptimizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  fileType?: string;
}

const defaultOptions: OptimizeOptions = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.9,
  fileType: "image/webp"
};

const useOptimiceImageFile = () => {
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const optimiceImageFile = (
    file: File,
    customOptions?: OptimizeOptions
  ): Promise<File> => {
    const options = { ...defaultOptions, ...customOptions };
    const originalSize = file.size;
    const SIZE_THRESHOLD = 200 * 1024; // 500KB en bytes

    // Si el archivo es menor a 500KB, retornar el original
    if (originalSize < SIZE_THRESHOLD) {
      console.log("📦 Image size is under 500KB:");
      console.log(`Current size: ${formatBytes(originalSize)}`);
      console.log("Skipping optimization");
      return Promise.resolve(file);
    }

    // solo puede optimizar imagenes de 500kb o mas
    if (!file.type.includes("image")) {
      return Promise.resolve(file);
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(img.src);

        // Calculate new dimensions
        let { width, height } = img;
        if (width > options.maxWidth!) {
          height = (height * options.maxWidth!) / width;
          width = options.maxWidth!;
        }
        if (height > options.maxHeight!) {
          width = (width * options.maxHeight!) / height;
          height = options.maxHeight!;
        }

        // Create canvas and resize
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          blob => {
            if (blob) {
              const newFile = new File([blob], file.name, {
                type: options.fileType,
                lastModified: Date.now()
              });

              const reduction = ((originalSize - newFile.size) / originalSize) * 100;
              console.log("📦 Image compression results:");
              console.log(`Original size: ${formatBytes(originalSize)}`);
              console.log(`Compressed size: ${formatBytes(newFile.size)}`);
              console.log(`Reduction: ${reduction.toFixed(2)}%`);
              console.log("level", options.quality);

              resolve(newFile);
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          options.fileType,
          options.quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error("Failed to load image"));
      };
    });
  };

  return { optimiceImageFile };
};

export default useOptimiceImageFile;
